import git from './utils/git.json';

/**
 * Env values are strings, so `Boolean("false")` would be `true`. Only `true` and `1` switch a flag on.
 */
function envFlag(value: string | undefined): boolean {
    return value === 'true' || value === '1';
}

/**
 * This variable contains all the build constants that will be changed between customer deployments.
 *
 * These variables require additional alignment with the team and should be miniimzed.
 * We should first assess if these values can be derived, predicted or detected at runtime.
 *
 * Do not add constants that we do not intend to change. It's not a placeholder for everything we might want to change
 * in the future. It should contain only variables that are explicitly mentioned to change between customer deployments
 * or that are specifically expected to be different between customer environments for a good reason.
 */
export const B = {
    APP_VERSION: git.gitCommitHash || '',

    //
    // Infrastructure
    //
    INGESTION_HOST: import.meta.env.VITE_INGESTION_HOST, // legacy, to be phased-out
    MATERIALIZED_HOST: import.meta.env.VITE_MATERIALIZED_HOST, // Not each environment has a predictable value here
    AGENT_CHAT_HOST: import.meta.env.VITE_AGENT_CHAT_HOST, // legacy, not needed, can be inferred from project_id
    APP_URL: import.meta.env.VITE_URL || 'http://localhost:3008',

    //
    // Build target
    //
    APP_IS_DEV: Boolean(import.meta.env.DEV),
    APP_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT || '?',
    APP_CUSTOMER: import.meta.env.VITE_CUSTOMER || '?',
    UNDER_CONSTRUCTION: envFlag(import.meta.env.VITE_UNDER_CONSTRUCTION), // Rare build for mission-critical maintenance
    HIBERNATE_APP: envFlag(import.meta.env.VITE_HIBERNATE_APP), // Special build once customer's contract ended
    /**
     * MSW API mocking in the browser (dev only)
     */
    ENABLE_MSW: Boolean(import.meta.env.DEV) && envFlag(import.meta.env.VITE_ENABLE_MSW),

    //
    // External services (public shareable keys, preferable limited to the domain of this deployment)
    //
    APP_ROLLBAR_ACCESS_TOKEN: import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN || '',

    AUTH0_CLIENT_ID: import.meta.env.VITE_AUTH0_CLIENT_ID || '',
    AUTH0_DOMAIN: import.meta.env.VITE_AUTH0_DOMAIN || '',
    AUTH0_ORGANIZATION: import.meta.env.VITE_AUTH0_ORGANIZATION || '',

    FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY || '',
    FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
    FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
    FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
    FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID || '',
    FIREBASE_MEASUREMENT_ID: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',

    FRONTEGG_APPLICATION_ID: import.meta.env.VITE_FRONTEGG_APPLICATION_ID || '',
    FRONTEGG_CLIENT_ID: import.meta.env.VITE_FRONTEGG_CLIENT_ID || '',
    FRONTEGG_DOMAIN: import.meta.env.VITE_FRONTEGG_DOMAIN || '',

    /**
     * Legacy
     * Use the showcase dataset for payment terms instead of the real databag.
     * TODO: Remove
     */
    PAYMENT_TERMS_SHOWCASE: Boolean(import.meta.env.VITE_PAYMENT_TERMS_SHOWCASE || ''),

    /**
     * Legacy
     * TODO: Move this to BE config
     */
    DISABLE_ALL_CARD_TAGS: Boolean(import.meta.env.VITE_DISABLE_ALL_CARD_TAGS || ''),

    /**
     * IAP-protected staff-management app of this environment (cat-infra/apps/staff-management).
     * Set manually per environment; the dev menu only shows the link when it is set.
     * TODO: Deduce from project
     */
    STAFF_MANAGEMENT_URL: import.meta.env.VITE_STAFF_MANAGEMENT_URL || '',

    /**
     * Legacy
     * TODO: Remove
     */
    APP_PING_INTERVAL: import.meta.env.VITE_PING_INTERVAL || '',
}

/**
 * staging or dev environment
 */
export const ONLY_FOR_US: true | undefined = B.APP_ENVIRONMENT === 'staging' || B.APP_IS_DEV || undefined;

/**
 * Resolve a service URL and ensure it is not localhost in production.
 */
export function resolveServiceUrl(key: keyof typeof B): URL | null {
    const url = B[key];
    if (typeof url !== 'string' || !url) return null;
    const urlObj = new URL(url);
    if (!B.APP_IS_DEV) {
        // Ensure no local setups are used on deployed instances
        if (urlObj.host === 'localhost' || urlObj.host === '127.0.0.1') {
            console.error(`resolveServiceUrl(${key}): URL is only allowed for development ("${url}")`);
            return null;
        }
    }
    return urlObj;
}

export function projectIdWithFallback() {
    return B.FIREBASE_PROJECT_ID || (B.APP_CUSTOMER + '-' + B.APP_ENVIRONMENT);
}
